import { parseValue } from "./data-utils";

export interface TempRecord {
    [key: string]: any;
}


/**
 * Converts a camelCase identifier into an UPPER_SNAKE_CASE variant.
 * @param value Input string using camelCase formatting.
 * @returns Upper snake case version of the supplied value.
 */
export const convertCamelToUpperSnake = (value: string) => {
    return value.split(/(?=[A-Z])/).join('_').toUpperCase();
}

/**
 * Lowers the second consecutive uppercase character sequence to prevent duplicate casing.
 * @param str Candidate string to normalize.
 * @returns String with the second uppercase character in a streak converted to lowercase.
 */
function replaceSecondUpper(str: string) {
    // Initialize a counter for uppercase characters
    let upperCount = 0;
    let tempStr = str;
    // Loop through each character of the string
    if (str) {
        for (let i = 0; i < str?.length; i++) {
            // If the character is uppercase
            if (str[i] === str[i].toUpperCase()) {
                // Increment the counter
                upperCount++;
                // If the counter is 2, replace the character with lowercase and return the result
                if (upperCount === 2) {
                    upperCount = 0;
                    str = str.slice(0, i) + str[i].toLowerCase() + str.slice(i + 1);

                }
            } else {
                upperCount = 0;
            }
        }

    }
    // If no second uppercase character is found, return the original string
    return str;
}


/**
 * Normalizes a field name for case conversions by lowering the second uppercase streak and removing parentheses.
 * @param value Raw field identifier.
 * @returns Sanitized name or an empty string when the input is falsy.
 */
export const cleanFieldName = (value: string) => {
    if (value) {
        value = replaceSecondUpper(value)
        return value.replace(/[\(\)]/g, '')
    }
    return '';
}

    export const cleanHeading = (name: string) => {
        name = name.replace(/[.]/g, "").trim()
        name = name.replace(/[-]/g, " ").trim()
        return name;
    }

/**
 * Converts a string into camelCase, removing spacing and punctuation.
 * @param text Source text to transform.
 * @returns Camel case representation of the input.
 */
export const toCamelCase = (text: string) => {

    text = cleanHeading(text)
    if (text.length === 0)
        return text;

    text = cleanFieldName(text)
    // replace any non-word character or underscore with a space
    text = text.replace(/\W|_/g, ' ');
    // capitalize the first letter of each word except the first one
    text = text.replace(/\s\b(\w)/g, (match, letter) => letter.toUpperCase());
    //first character must be lower case.
    text = text[0].toLowerCase() + text.slice(1);
    // remove any remaining spaces
    text = text.replace(/\s/g, '');
    // return the result
    return text;
}


/**
 * Capitalizes the first character of the provided text while lowering the rest.
 * @param text String to capitalize.
 * @returns Title-cased word with only the first character in uppercase.
 */
export const capitalizeFirstLetter = (text: string) => {
    text = text[0].toUpperCase() + text.slice(1).toLowerCase();
    return text;
}

/**
 * Transforms a string into PascalCase by capitalizing each word and stripping delimiters.
 * @param text Source text to convert.
 * @returns Pascal case variant of the text.
 */
export const toPascalCase = (text: string) => {
    text = cleanFieldName(text)
    // replace any non-word character or underscore with a space
    text = text.replace(/\W|_/g, ' ');
    // capitalize the first letter of each word
    text = text.replace(/\b(\w)/g, (match, letter) => letter.toUpperCase());

    // first character must be upper case
    text = text[0].toUpperCase() + text.slice(1);
    // remove the first space if any
    text = text.replace(/^\s/, '');
    // remove any remaining spaces
    text = text.replace(/\s/g, '');
    // return the result
    return text;
}

/**
 * Converts a string into snake_case, collapsing redundant separators.
 * @param text Source text to normalize.
 * @returns Snake case version of the input.
 */
export const toSnakeCase = (text: string) => {
    text = cleanFieldName(text)
    // Replace any hyphen with an underscore
    text = text.replace(/-/g, '_');
    // Replace any uppercase letter with an underscore and a lowercase letter
    text = text.split(/(?=[A-Z]{1})/).join('_').toLowerCase();
    // replace any non-word character or uppercase letter with an underscore and a lowercase letter
    text = text.replace(/\W| ( [A-Z])/g, (match, letter) => letter ? `_${letter.toLowerCase()}` : '_');
    // remove any leading or trailing underscores
    text = text.replace(/^_+|_+$/g, '');
    // Replace any spaces with an underscore
    text = text.replace(/\s/g, '_');
    // Replace duplicates with a single
    text = text.replace(/_{2,}/g, "_")
    // return the result
    return text;
}

/**
 * Determines whether the supplied string consists solely of uppercase characters and whitespace.
 * @param text String to evaluate.
 * @returns True when the string is all capital letters (plus spaces), otherwise false.
 */
export const isAllCaps = (text: string) => {
    return /^[A-Z\s]+$/.test(text);
}


/**
 * Converts a string into kebab-case, collapsing duplicate separators and normalizing uppercase content.
 * @param text Source text to transform.
 * @returns Kebab case variant of the text.
 */
export const toKebabCase = (text: string) => {
    text = cleanFieldName(text)
    // Replace any underscore with a hyphen
    text = text.replace(/_/g, '-');

    //If all caps then make all lowercase.
    let isCaps = isAllCaps(text)
    if (isCaps)
        text = text.toLowerCase();

    // Replace any uppercase letter with a hyphen and a lowercase letter
    text = text.split(/(?=[A-Z]{1})/).join('-').toLowerCase();
    // replace any non-word character or uppercase letter with a hyphen and a lowercase letter
    text = text.replace(/\W| ( [A-Z])/g, (match, letter) => letter ? `-${letter.toLowerCase()}` : '-');
    // remove any leading or trailing hyphens
    text = text.replace(/^-+|-+$/g, '');
    // Replace any spaces with an hyphen
    text = text.replace(/\s/g, '-');
    // Replace duplicates with a single
    text = text.replace(/-{2,}/g, "-")
    // return the result
    return text;
}



/**
 * Removes all whitespace characters from the provided value.
 * @param value Text to strip of spaces.
 * @returns String without whitespace characters.
 */
export const removeSpaces = (value: string) => {
    return value.replace(/\s/g, '')
}

/**
 * Converts a string to uppercase snake case by replacing hyphens and applying upper casing.
 * @param value Text to translate into snake case.
 * @returns Uppercase snake case representation.
 */
export const tranCase = (value: string) => {
    value = value.replace(/-/g, '_');
    return value.toUpperCase();
}





export function camelCaseObj(val: any) {
    let result = val;

    if (val !== undefined && val !== null) {
        if (Array.isArray(val)) {
            let camelArray = new Array<TempRecord>();
            val.forEach(x => {
                let camelItem = camelCaseObj(x);
                camelArray.push(camelItem)
            })
            result = camelArray;
        }
        else {
            let camelRecord: TempRecord = {};
            for (const [key, value] of Object.entries(val)) {
                if (value === null || value === undefined)
                    camelRecord[toCamelCase(key)] = value
                if (typeof value === 'object') {
                    if (value instanceof Date)
                        camelRecord[toCamelCase(key.toLowerCase())] = value
                    else
                        camelRecord[toCamelCase(key.toLowerCase())] = camelCaseObj(value)
                } else {
                    camelRecord[toCamelCase(key.toLowerCase())] = parseValue(value)?.value
                }
            }
            result = camelRecord;
        }
    }


    return result;
}